import {
  IAddPanelFilterPayload,
  IToggleEnabledPayload,
  IUpdateQuickFilterPayload,
  IInitializeFiltersPayload,
  IUpdatePanelFilterPayload,
  IDeletePanelFilterPayload,
  IUpdateTemporaryFilterPayload,
} from "../interfaces/payloads/filterReducers";
import { createModel } from "@rematch/core";
import i18n from "../../i18n";

import { IFiltersState } from "../interfaces/states/IFiltersState";
import produce from "immer";
import difference from "lodash/difference";
import throttle from "lodash/throttle";
import uniq from "lodash/uniq";
import { getUserFiltersFromNVSAsync, setUserFiltersToNVSAsync } from "src/backend/services/NamedValueStorageService";
import { IFilter, IFilterGroup } from "src/backend/interfaces/filters/IFilter";
import { WARN } from "src/gantt/components/common/NotificationMessage";
import { iRootState, Dispatch } from "../store";
import { parseFilterFromQueryString } from "src/backend/helpers/urlHelper";
import { applyUrlFilter } from "src/gantt/helpers/FilterUtils";

export default createModel()({
  state: {
    staticFilters: [], // Filters defined in configuration, not editable by users
    panelFilters: [], // Filters saved via filter panel
    enabledPanelFilters: [], // Different set of panelfilters can be enabled in different views. This is a list of enabled panelfilters indexes for each view.
    quickFilters: [], // Filtering items by a search string, comparing all properties of each item
    temporaryFilters: [], // The filters being created in the filter panel. Press "Filter" in order to apply hcoices.
  } as IFiltersState,
  reducers: {
    initializeFilters: (state: IFiltersState, payload: IInitializeFiltersPayload): IFiltersState => {
      return {
        ...state,
        staticFilters: payload.staticFilters ? payload.staticFilters : [],
        panelFilters: payload.panelFilters ? payload.panelFilters : Array(payload.viewsCount).fill([]),
        enabledPanelFilters: Array(payload.viewsCount).fill([]),
        quickFilters: Array(payload.viewsCount).fill({ value: "" }),
        temporaryFilters: Array(payload.viewsCount).fill(null),
      };
    },

    _updateQuickFilter: produce((state: IFiltersState, { value, viewIndex }: IUpdateQuickFilterPayload) => {
      state.quickFilters[viewIndex].value = value;
    }),

    _addPanelFilter: produce((state: IFiltersState, payload: IAddPanelFilterPayload) => {
      const filterIndex = state.panelFilters[payload.viewIndex]?.findIndex((fg) => fg.key === payload.filterGroup.key) ?? -1;
      if (filterIndex < 0) {
        if(!state.panelFilters[payload.viewIndex]) {
          state.panelFilters[payload.viewIndex] = [];
        }
        state.panelFilters[payload.viewIndex].push(payload.filterGroup);
      }
    }),
    _setPanelFilters: (state: IFiltersState, viewCount: number, filters: IFilterGroup[][]): IFiltersState => {
      const enabledPanelFilters = [];
      for(const filter of filters) {
        const enabledViewFilters = [];
        for(let j = 0; j < filter?.length; j++) {
          if(filter[j].locked) {
            enabledViewFilters.push(j);
          }
        }
        enabledPanelFilters.push(enabledViewFilters);
      }
      while(enabledPanelFilters.length < viewCount) {
        enabledPanelFilters.push([]);
      }
      return { ...state, panelFilters: filters, enabledPanelFilters: enabledPanelFilters };
    },
    _setUrlFilters: (state: IFiltersState, urlState: IFiltersState): IFiltersState => {
      return { 
        ...state, 
        panelFilters: urlState.panelFilters, 
        enabledPanelFilters: urlState.enabledPanelFilters,
        quickFilters: urlState.quickFilters,
        temporaryFilters: urlState.temporaryFilters,
      };
    },
    _updatePanelFilter: produce((state: IFiltersState, payload: IUpdatePanelFilterPayload) => {
      const filterIndex = state.panelFilters[payload.viewIndex]?.findIndex((fg) => fg.key === payload.filterGroup.key) ?? -1;
      if (filterIndex >= 0) {
        state.panelFilters[payload.viewIndex][filterIndex] = payload.filterGroup;
      }
    }),
    _deletePanelFilter: produce((state: IFiltersState, payload: IDeletePanelFilterPayload) => {
      const filterIndex = state.panelFilters[payload.viewIndex]?.findIndex((f) => f.name === payload.filterName) ?? -1;
      if (filterIndex >= 0) {
        state.panelFilters[payload.viewIndex].splice(filterIndex, 1);

        // Remove from enabled filters in all views
        state.enabledPanelFilters[payload.viewIndex] = state.enabledPanelFilters[payload.viewIndex].filter((i) => i !== filterIndex);
      }
    }),
    resetPanelFilters: (state: IFiltersState, _ = null): IFiltersState => {
      return { ...state, panelFilters: [], enabledPanelFilters: Array(state.enabledPanelFilters.length).fill([]) };
    },
    resetEnabledFilters: (state: IFiltersState, payload: { viewIndex: number }): IFiltersState => {

      let enabledFiltersCopy = [...state.enabledPanelFilters]; // create true copy

      if ( enabledFiltersCopy[payload.viewIndex] ) {
        enabledFiltersCopy[payload.viewIndex] = [];
      } 
      return { ...state,  enabledPanelFilters: enabledFiltersCopy };
    },

    toggleEnabled: produce((state: IFiltersState, payload: IToggleEnabledPayload) => {
      const filterIndex = state.panelFilters[payload.viewIndex]?.findIndex((f) => f.name === payload.filterName && f.ownerId === payload.ownerId) ?? -1;
      if (filterIndex >= 0) {
        const enableFilter = !state.enabledPanelFilters[payload.viewIndex].includes(filterIndex);
        if (!enableFilter) {
          // Set disabled
          state.enabledPanelFilters[payload.viewIndex] = difference(state.enabledPanelFilters[payload.viewIndex], [filterIndex]);
        } else {
          // Set enabled
          state.enabledPanelFilters[payload.viewIndex] = uniq(state.enabledPanelFilters[payload.viewIndex].concat([filterIndex]));
        }
        //Enable/Disable linked filters 
        const otherViews = difference(Array.from(Array(state.panelFilters.length).keys()),[payload.viewIndex]);
        for(const otherViewIndex of otherViews) {
          const otherFilterIndex = state.panelFilters[otherViewIndex]?.findIndex((f) => f.name === payload.filterName && f.ownerId === payload.ownerId) ?? -1;
          if(otherFilterIndex >= 0) {
            const otherFilterIsEnabled = state.enabledPanelFilters[otherViewIndex].includes(otherFilterIndex);
            if(otherFilterIsEnabled !== enableFilter) {
              if (otherFilterIsEnabled) {
                // Set disabled
                state.enabledPanelFilters[otherViewIndex] = difference(state.enabledPanelFilters[otherViewIndex], [otherFilterIndex]);
              } else {
                // Set enabled
                state.enabledPanelFilters[otherViewIndex] = uniq(state.enabledPanelFilters[otherViewIndex].concat([otherFilterIndex]));
              }
            }
          }
        }
      }
    }),
    toggleLocked: produce((state: IFiltersState, payload: IToggleEnabledPayload) => {
      const filterIndex = state.panelFilters[payload.viewIndex]?.findIndex((f) => f.name === payload.filterName && f.ownerId === payload.ownerId) ?? -1;
      if(filterIndex >= 0) {
        const filterLocked = !(state.panelFilters[payload.viewIndex][filterIndex].locked);
        state.panelFilters[payload.viewIndex][filterIndex].locked = filterLocked;
        if (filterLocked && !state.enabledPanelFilters[payload.viewIndex].includes(filterIndex)) {
          // Set enabled
          state.enabledPanelFilters[payload.viewIndex] = uniq(state.enabledPanelFilters[payload.viewIndex].concat([filterIndex]));
        }
        //Enable&lock linked filters 
        const otherViews = difference(Array.from(Array(state.panelFilters.length).keys()),[payload.viewIndex]);
        for(const otherViewIndex of otherViews) {
          const otherFilterIndex = state.panelFilters[otherViewIndex]?.findIndex((f) => f.name === payload.filterName && f.ownerId === payload.ownerId) ?? -1;
          if(otherFilterIndex >= 0) {
            state.panelFilters[otherViewIndex][otherFilterIndex].locked = filterLocked;
            if(filterLocked && !state.enabledPanelFilters[otherViewIndex].includes(otherFilterIndex)) {
              // Set enabled
              state.enabledPanelFilters[otherViewIndex] = uniq(state.enabledPanelFilters[otherViewIndex].concat([otherFilterIndex]));
            }
          }
        }
      }
    }),
    updateTemporaryFilter: produce((state: IFiltersState, payload: IUpdateTemporaryFilterPayload) => {
      state.temporaryFilters[payload.viewIndex] = payload.filterGroup;
    }),
    resetTemporaryFilter: produce((state: IFiltersState, payload: { viewIndex: number }) => {
      state.temporaryFilters[payload.viewIndex] = null;
    }),
  },
  effects: (dispatch0) => ({
    updateQuickFilter: throttle(dispatch0.filters._updateQuickFilter, 250, { leading: false, trailing: true }),
    loadPanelFiltersAsync: async (_: null, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      dispatch.filters.resetPanelFilters();
      const filters = await getUserFiltersFromNVSAsync(
        rootState.common.configuration.baseConfiguration,
        rootState.common.configuration.userId
      );
      const filterParam = parseFilterFromQueryString();
      if(filterParam) {
        const urlFilters = applyUrlFilter(filterParam, filters);
        dispatch.filters._setUrlFilters(urlFilters);
      } else {
        dispatch.filters._setPanelFilters(rootState.common.configuration.viewConfigurations.length, filters);
      }
    },
    savePanelFiltersAsync: async (_: null, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      try {
        const filterGroups = rootState.filters.panelFilters;
        await setUserFiltersToNVSAsync(rootState.common.configuration.baseConfiguration, rootState.common.configuration.userId, filterGroups);
      } catch (err) {
        dispatch.UI.showMessage(i18n.t("error.savefilter", {err: err}), WARN);
      }
    },
    savePanelFilterAsync: async (payload: IAddPanelFilterPayload, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      try {
        const filterGroups = [...rootState.filters.panelFilters];
        let viewFilters   = filterGroups[payload.viewIndex] ?? [];

        // update payload.filterGroup in viewFilters if already exist
        if ( viewFilters.findIndex( fg => fg.key === payload.filterGroup.key ) !== -1 ) {
          viewFilters = viewFilters.map((fg) => 
            fg.key === payload.filterGroup.key
              ? { ...payload.filterGroup }
              : { ...fg }
          )
        } else {
          viewFilters = [...viewFilters, payload.filterGroup];
        }

        filterGroups[payload.viewIndex] = viewFilters;
        await setUserFiltersToNVSAsync(rootState.common.configuration.baseConfiguration, rootState.common.configuration.userId, filterGroups);
        dispatch.filters._addPanelFilter(payload);
        dispatch.filters._updatePanelFilter(payload);
      } catch (err) {
        dispatch.UI.showMessage(i18n.t("error.savefilter", {err: err}), WARN);
      }
    },
    deletePanelFilterAsync: async (payload: IDeletePanelFilterPayload, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      try {
        const filterGroups = [...rootState.filters.panelFilters];
        let viewFilters = filterGroups[payload.viewIndex] ?? [];
        viewFilters = viewFilters.filter((f: IFilterGroup) => f.name !== payload.filterName);
        filterGroups[payload.viewIndex] = viewFilters;
        await setUserFiltersToNVSAsync(rootState.common.configuration.baseConfiguration, rootState.common.configuration.userId, filterGroups);
        dispatch.filters._deletePanelFilter(payload);
      } catch (err) {
        dispatch.UI.showMessage(i18n.t("error.deletefilter", {err: err}), WARN);
      }
    },
  }),
});
